import type {
  ContainerQueryParams,
  SocialMediaWizardContainerQuery,
  SocialMediaWizardMediaTypeSelection,
  WizardMobileNavView,
} from '@Components/social-media-wizard/social-media-wizard.types';
import type {WizardErrorBanner} from '@Components/social-media-wizard/social-media-wizard.types';
import {SocialMediaWizardStep} from '@Components/social-media-wizard/social-media-wizard.types';
import {useRef} from 'react';
import {useContainerQuery} from '@Hooks/useContainerQuery';
import {containerQuery} from '@Components/social-media-wizard/social-media-wizard-container-queries-context';
import type {SocialAccount} from '@Components/social-media/account.vo';
import type {GraphicsMetaMapType, SocialPostPublishingAction} from '@Components/social-media/publish-params.types';
import type {
  AccountNeedsResizeMap,
  AccountPublishableUrlForAllMediaTypeMap,
  CaptionErrorByAccountIds,
  DesignNeedsResizeInformation,
  DesignSelection,
  TitleErrorByAccountIds,
} from '@Components/social-media/social-media.types';
import {PosterResizeRequiredStatus} from '@Components/social-media/social-media.types';
import {usePublishingStatusForAllAccounts} from '@Components/social-media/social-media.hooks';
import {LoadingStates} from '@Utils/loading.util';
import type {MediaTypePublishingParams, SocialMediaAccountId} from '@Components/social-media/post.vo';
import {PlatformMediaType} from '@Components/social-media/post.vo';
import {AccountResizeStatus} from '@Components/social-media-wizard/components/social-media-resize-options/social-media-resize-options.types';
import type {ErrorBoxInputs} from '@Components/error-box';
import type {DesignSelectorTab} from '@Components/design-selector/design-selector.types';
import {useAppSelector} from '@/hooks';

export const useSocialMediaWizardContainerQuery = (): ContainerQueryParams => {
  const containerRef = useRef<HTMLDivElement>(null);
  const params = useContainerQuery(containerRef, containerQuery);

  const containerQueryParamProps: SocialMediaWizardContainerQuery = {
    isExtraSmallScreen: params.xsmall,
    isSmallScreen: params.small,
    isMediumScreen: params.medium,
    isLargeScreen: params.large,
    isExtraLargeScreen: params.xlarge,
  };

  return {
    containerQueryParamProps,
    containerRef,
    hasContainerQueryInitialized: params.small !== undefined,
  };
};

export const useWizardMobileNavView = (): WizardMobileNavView => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.wizardMobileNavView;
  });
};

export const useHasSeenConfetti = (): boolean => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.hasSeenConfetti;
  });
};

export const usePlatformSelectionforAccount = (accountId: number): SocialMediaWizardMediaTypeSelection => {
  return useAppSelector((state): SocialMediaWizardMediaTypeSelection => {
    return state.socialMediaWizard.platformSelectionForAccount[accountId];
  });
};

export const useIsTemplateSelected = (): boolean => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.isTemplateSelected;
  });
};

export const useIsSocialMediaWizardAiCaptionsEnabled = (): boolean => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.isSocialMediaWizardAiCaptionsEnabled;
  });
};

export const useIsGoogleBusinessProfileEnabled = (): boolean => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.isGoogleBusinessProfileEnabled;
  });
};

export const useAreAccountsLoading = (): boolean => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.areAccountsLoading;
  });
};

export const useHasPublishingFinished = (): boolean => {
  const publishingStatuses = usePublishingStatusForAllAccounts();

  return useAppSelector((state) => {
    if (state.socialMediaWizard.wizardStep !== SocialMediaWizardStep.PUBLISH) {
      return false;
    }

    const accounts = state.socialMediaWizard.accountIdsToPublishTo;

    for (const accountId of accounts) {
      const publishingStatusesForAccount = publishingStatuses[accountId];
      const isPostLoaded = publishingStatusesForAccount[PlatformMediaType.POST] !== LoadingStates.LOADING;
      const isReelLoaded = publishingStatusesForAccount[PlatformMediaType.REELS] !== LoadingStates.LOADING;
      const isStoryLoaded = publishingStatusesForAccount[PlatformMediaType.STORIES] !== LoadingStates.LOADING;

      if (!isPostLoaded || !isReelLoaded || !isStoryLoaded) {
        return false;
      }
    }

    return true;
  });
};

export const useHideNextButton = (): boolean => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.hideNextButton;
  });
};

export const useNumParentAccountsConnected = (): number => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.numParentAccountsConnected;
  });
};

export const useWizardCaptionErrors = (): CaptionErrorByAccountIds => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.captionError;
  });
};

export const useWizardTitleErrors = (): TitleErrorByAccountIds => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.titleError;
  });
};

export const useWizardConnectedAccounts = (): SocialAccount[] => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.accounts;
  });
};

export const useWizardAccountsToPublishTo = (): number[] => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.accountIdsToPublishTo;
  });
};

export const useWizardPublishingAction = (): SocialPostPublishingAction | null => {
  return useAppSelector((state) => {
    if (state.socialMediaWizard.publishingAction) {
      return state.socialMediaWizard.publishingAction;
    }

    return null;
  });
};

export const useDesignsToPublish = (): DesignSelection[] => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.designsToPublish;
  });
};

export const useWizardPublishingParams = (): Record<SocialMediaAccountId, MediaTypePublishingParams> => {
  return useAppSelector((state): Record<SocialMediaAccountId, MediaTypePublishingParams> => {
    return state.socialMediaWizard.publishingParams;
  });
};

export const useWizardDesignsGraphicsMetaMap = (): GraphicsMetaMapType => {
  return useAppSelector((state): GraphicsMetaMapType => {
    return state.socialMediaWizard.designsGraphicsMetaMap;
  });
};

export const useNeedsResizeMap = (): AccountNeedsResizeMap => {
  return useAppSelector((state): AccountNeedsResizeMap => {
    return state.socialMediaWizard.accountNeedsResizeMap;
  });
};

export const useAccountNeedsResizeMap = (accountId: SocialMediaAccountId | undefined): DesignNeedsResizeInformation | null => {
  return useAppSelector((state): DesignNeedsResizeInformation | null => {
    if (!accountId || !state.socialMediaWizard.accountNeedsResizeMap?.[accountId]) {
      return null;
    }

    return state.socialMediaWizard.accountNeedsResizeMap[accountId];
  });
};

export const useWizardDesignsGraphicMetaMap = (): GraphicsMetaMapType => {
  return useAppSelector((state): GraphicsMetaMapType => {
    return state.socialMediaWizard.designsGraphicsMetaMap;
  });
};

export const useIsPublishingVideoForAccount = (): boolean => {
  return useAppSelector((state): boolean => {
    const designsGraphicsMetaMap = state.socialMediaWizard.designsGraphicsMetaMap;

    if (!designsGraphicsMetaMap) {
      return false;
    }

    return Object.values(designsGraphicsMetaMap).some((pageGraphicMeta) => {
      return Object.values(pageGraphicMeta).some((graphicMeta) => {
        return graphicMeta.isVideo;
      });
    });
  });
};

export const useDesignSelectorTab = (): DesignSelectorTab => {
  return useAppSelector((state): DesignSelectorTab => {
    return state.socialMediaWizard.designSelectorTab;
  });
};

export const usePublishableURLsForAccount = (): AccountPublishableUrlForAllMediaTypeMap => {
  return useAppSelector((state): AccountPublishableUrlForAllMediaTypeMap => {
    return state.socialMediaWizard.publishableGraphicUrlForAllMediaTypeMap;
  });
};

export const useDesignsGraphicsMetaMapForAccountWithMediaType = (accountId: SocialMediaAccountId, platformMediaType: PlatformMediaType): GraphicsMetaMapType => {
  return useAppSelector((state): GraphicsMetaMapType => {
    const mediaTypePublishingParams = state.socialMediaWizard.publishingParams[accountId];

    const publishingParams = mediaTypePublishingParams[platformMediaType];

    if (!publishingParams?.graphicsMetaMap) {
      return state.socialMediaWizard.designsGraphicsMetaMap;
    }

    return publishingParams.graphicsMetaMap;
  });
};

export const useIsAnyPosterResizingForAccountWithMediaType = (accountId: SocialMediaAccountId, platformMediaType: PlatformMediaType): boolean => {
  return useAppSelector((state): boolean => {
    const resizingStatusForPosters = state.socialMediaWizard.accountResizeStatusMapForMediaType[accountId][platformMediaType];

    return Object.values(resizingStatusForPosters).some((resizingStatusForPage) => {
      return Object.values(resizingStatusForPage).some((resizingStatusForPage) => {
        return resizingStatusForPage === AccountResizeStatus.RESIZING;
      });
    });
  });
};

export const useIsAnyPosterResizingForAccount = (accountId: SocialMediaAccountId): boolean => {
  return useAppSelector((state): boolean => {
    const resizingStatusForPosters = state.socialMediaWizard.accountResizeStatusMapForMediaType[accountId];
    const platformMediaTypes: PlatformMediaType[] = [PlatformMediaType.POST, PlatformMediaType.STORIES, PlatformMediaType.REELS];

    return platformMediaTypes.some((platformMediaType) => {
      const resizingPosterStatusForMediaType = resizingStatusForPosters[platformMediaType];
      return Object.values(resizingPosterStatusForMediaType).some((resizingPageStatus) => {
        return Object.values(resizingPageStatus).some((resizeStatus) => {
          return resizeStatus === AccountResizeStatus.RESIZING;
        });
      });
    });
  });
};

const isResizeRequiredForPoster = (accountResizeRequiredStatus: PosterResizeRequiredStatus): boolean => {
  return accountResizeRequiredStatus === PosterResizeRequiredStatus.RESIZE_NEEDED;
};

export const useDoesAnyPosterRequireResizeForAccount = (accountId: SocialMediaAccountId | undefined, platformMediaType: PlatformMediaType): boolean => {
  return useAppSelector((state): boolean => {
    if (!accountId) {
      return false;
    }

    const resizeRequiredForAccount = state.socialMediaWizard.accountNeedsResizeMap[accountId];
    const publishingParams = state.socialMediaWizard.publishingParams[accountId];
    const {accounts} = state.socialMediaWizard;

    if (!publishingParams) {
      return false;
    }

    const specificAccount = accounts.find((account) => {
      return account.id === accountId;
    });

    if (!specificAccount) {
      console.error('No account with id: ', accountId, ' found');
      return false;
    }

    if (!resizeRequiredForAccount) {
      return false;
    }

    return Object.values(resizeRequiredForAccount).some((pageResizeInformation) => {
      return Object.values(pageResizeInformation).some((resizeStatus) => {
        if (!resizeStatus) {
          console.error('No resize information found for poster');
          return false;
        }

        const resizeRequiredStatus = {
          [PlatformMediaType.POST]: resizeStatus.needsResizing,
          [PlatformMediaType.REELS]: resizeStatus.reelNeedsResizing,
          [PlatformMediaType.STORIES]: resizeStatus.storyNeedsResizing,
        }[platformMediaType];

        return !!(resizeRequiredStatus && isResizeRequiredForPoster(resizeRequiredStatus));
      });
    });
  });
};

export const useHasAnyPosterResizeErroredForAccount = (accountId: SocialMediaAccountId, platformMediaType: PlatformMediaType | undefined): boolean => {
  return useAppSelector((state): boolean => {
    const resizingStatusForPosters = state.socialMediaWizard.accountResizeStatusMapForMediaType[accountId][platformMediaType ?? PlatformMediaType.POST];

    return Object.values(resizingStatusForPosters).some((resizingStatusForPages) => {
      return Object.values(resizingStatusForPages).some((resizeStatus) => {
        return resizeStatus === AccountResizeStatus.RESIZE_ERROR;
      });
    });
  });
};

export const useIsAnyPosterLoadingForAccount = (accountId: SocialMediaAccountId, platforMediaType: PlatformMediaType): boolean => {
  return useAppSelector((state): boolean => {
    const publishingParamsForAccount = state.socialMediaWizard.publishingParams[accountId];

    if (!publishingParamsForAccount) {
      return false;
    }

    const publishingParamsForPlatform = publishingParamsForAccount[platforMediaType];

    if (!publishingParamsForPlatform) {
      return false;
    }

    const designsGraphicMetaForAccount = publishingParamsForPlatform.graphicsMetaMap;

    if (!designsGraphicMetaForAccount) {
      console.error('No graphics meta found for account.');
      return false;
    }

    return Object.values(designsGraphicMetaForAccount).some((pageGraphicsMetaMap) => {
      return Object.values(pageGraphicsMetaMap).some((graphicMeta) => {
        return graphicMeta.graphicURL === '';
      });
    });
  });
};

export const useAccountLevelErrorMessage = (): ErrorBoxInputs => {
  return useAppSelector((state): ErrorBoxInputs => {
    return state.socialMediaWizard.accountLevelErrorMessage;
  });
};

export const useWizardErrorBanner = (): WizardErrorBanner | null => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.errorBanner;
  });
};

export const getPostId = (): string | null => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.post?.id ?? null;
  });
};

export const useAreCaptionsBeingAutoGenerated = (): boolean => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.areCaptionsBeingAutoGenerated;
  });
};

export const useGraphicsReadiness = (): LoadingStates => {
  return useAppSelector((state) => {
    return state.socialMediaWizard.graphicsReadiness.status;
  });
};
